import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { ClientModeProvider } from "@/lib/clientMode";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import FabricsListPage from "@/pages/FabricsListPage";
import FabricFormPage from "@/pages/FabricFormPage";
import FabricImportPage from "@/pages/FabricImportPage";
import ClientsListPage from "@/pages/ClientsListPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import ClientFormPage from "@/pages/ClientFormPage";
import WorkersListPage from "@/pages/WorkersListPage";
import WorkerDetailPage from "@/pages/WorkerDetailPage";
import TrimsListPage from "@/pages/TrimsListPage";
import TrimsFormPage from "@/pages/TrimsFormPage";
import OrdersListPage from "@/pages/OrdersListPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import NewOrderPage from "@/pages/NewOrderPage";
import FinancesPage from "@/pages/FinancesPage";
import ProductionPage from "@/pages/ProductionPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import PODetailPage from "@/pages/PODetailPage";
import VendorInvoicesPage from "@/pages/VendorInvoicesPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import FeedbackPage from "@/pages/FeedbackPage";
import AlterationsListPage from "@/pages/AlterationsListPage";
import NewAlterationPage from "@/pages/NewAlterationPage";
import AlterationDetailPage from "@/pages/AlterationDetailPage";
import CashVouchersPage from "@/pages/CashVouchersPage";
import StyleAdvisorPage from "@/pages/StyleAdvisorPage";
import ReferralsPage from "@/pages/ReferralsPage";
import ForecastingPage from "@/pages/ForecastingPage";
import VendorScorecardPage from "@/pages/VendorScorecardPage";
import AlterationPatternsPage from "@/pages/AlterationPatternsPage";
import WorkerReportsPage from "@/pages/WorkerReportsPage";
import LookbooksPage from "@/pages/LookbooksPage";
import PopUpEventsPage from "@/pages/PopUpEventsPage";
import AnalyticsDashboardPage from "@/pages/AnalyticsDashboardPage";

function App() {
  return (
    <AuthProvider>
      <ClientModeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/book" element={<PublicBookingPage />} />
            <Route path="/book/:slug" element={<PublicBookingPage />} />
            <Route path="/feedback/:orderId" element={<FeedbackPage />} />

          {/* Protected app routes */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route
              path="clients"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <ClientsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/new"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <ClientFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/:id/edit"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <ClientFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/:id"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <ClientDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/referrals"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <ReferralsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="lookbooks"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <LookbooksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="lookbooks/:campaignId"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <LookbooksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fabrics"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <FabricsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fabrics/new"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <FabricFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fabrics/import"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <FabricImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fabrics/:id"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <FabricFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="orders"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <OrdersListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="orders/new"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <NewOrderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="orders/:id"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <OrderDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="alterations"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <AlterationsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="alterations/new"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <NewAlterationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="alterations/:id"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <AlterationDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="trims"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <TrimsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="trims/new"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <TrimsFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="trims/:id"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <TrimsFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="production"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <ProductionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="production/workers"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <WorkersListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="production/workers/new"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Production"]}>
                  <WorkerDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="production/workers/:id"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <WorkerDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="production/forecasting"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Production"]}>
                  <ForecastingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="production/alteration-patterns"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <AlterationPatternsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="production/worker-reports"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <WorkerReportsPage />
                </ProtectedRoute>
              }
            />
            <Route path="invoices" element={
              <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                <PurchaseOrdersPage />
              </ProtectedRoute>
            } />
            <Route path="invoices/vendor" element={
              <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                <VendorInvoicesPage />
              </ProtectedRoute>
            } />
            <Route path="invoices/vendor-scorecard" element={
              <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                <VendorScorecardPage />
              </ProtectedRoute>
            } />
            <Route path="purchase-orders/:id" element={
              <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                <PODetailPage />
              </ProtectedRoute>
            } />
            <Route
              path="analytics"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <AnalyticsDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finances"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <FinancesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finances/cash-vouchers"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales", "Production"]}>
                  <CashVouchersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute allowedRoles={["Admin"]}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="appointments"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <AppointmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="style-advisor"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <StyleAdvisorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="events"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Sales"]}>
                  <PopUpEventsPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </ClientModeProvider>
  </AuthProvider>
  );
}

export default App;
